import { BrowserModule } from '@angular/platform-browser';
import { NgModule, CUSTOM_ELEMENTS_SCHEMA, inject, APP_INITIALIZER, provideAppInitializer, InjectionToken } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { AccordionModule } from 'primeng/accordion';
import { ButtonModule } from 'primeng/button';
import { RadioButtonModule } from 'primeng/radiobutton';
import { TableModule } from 'primeng/table';
import { PanelModule } from 'primeng/panel';
import { MultiSelectModule } from 'primeng/multiselect';
import { MenubarModule } from 'primeng/menubar';
import { InputTextModule } from 'primeng/inputtext';
import { ToastModule } from 'primeng/toast';
import { Select } from 'primeng/select';
import { MessageModule } from 'primeng/message';
import { ToggleButtonModule } from 'primeng/togglebutton';
import { DialogModule } from 'primeng/dialog';
import { DatePickerModule } from 'primeng/datepicker';
import { CardModule } from 'primeng/card';
import { TooltipModule } from 'primeng/tooltip';
import { CheckboxModule } from 'primeng/checkbox';
import { ChartModule } from 'primeng/chart';
import { ProgressBarModule } from 'primeng/progressbar';
import { DatePipe } from '@angular/common';
import { NgxSpinnerModule } from "ngx-spinner";
import { AppComponent } from './app.component';
import { AppGlobals } from './app.globals';
import { DelayedDispatchService } from './service/delayed-dispatch.service';
import { MenuComponent } from './menu/menu.component';
import { RouterModule, Routes } from '@angular/router';
import { AlloExtractsComponent } from './allo-extracts/allo-extracts.component';
import { AlloPropertiesComponent } from './allo-extracts/allo-properties/allo-properties.component';
import { AlloReportsComponent } from './allo-extracts/allo-reports/allo-reports.component';
import { AlloRateMasterComponent } from './allo-extracts/allo-rate-master/allo-rate-master.component';
import { AuthInterceptor } from './interceptor/auth-interceptor';
import { AlloHistoryComponent } from './allo-extracts/allo-history/allo-history.component';
import { SelectItem, MessageService } from 'primeng/api';
import { MatTabsModule } from '@angular/material/tabs';
import { FgaModule } from './fga/fga.module';
import { CommonModule } from '@angular/common'
//Msal
import { MsalService, MSAL_INSTANCE, MsalGuard, MsalInterceptor, MsalBroadcastService } from './msal';
import { IPublicClientApplication, PublicClientApplication, InteractionType } from '@azure/msal-browser';
import { MSAL_GUARD_CONFIG, MSAL_INTERCEPTOR_CONFIG } from './msal/constants';
import {
  MsalInterceptorConfiguration,
  MsalGuardConfiguration,
} from '@azure/msal-angular';
import { environment } from '../environments/environment';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatIconModule } from '@angular/material/icon';
import { UnauthorizedComponent } from './unauthorized/unauthorized.component';
import { provideApollo } from 'apollo-angular';
import { InMemoryCache } from '@apollo/client/cache';
import { providePrimeNG } from 'primeng/config';
import Nora from '@primeng/themes/nora';
import Lara from '@primeng/themes/lara';
import Aura from '@primeng/themes/aura';
import { CustomParameterInterceptor } from './interceptor/custom-parameter.interceptor';
import { ConstantsService } from './service/constants.service';
import { WebSocketService } from './service/websocket.service';
import { BreadcrumbComponent } from './core/breadcrumb/breadcrumb.component';
import { ToolbarComponent } from './core/toolbar/toolbar.component';
export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environment.CLIENT_ID,
      authority: environment.TENANT_ID,
      redirectUri: environment.REDIRECT_URL
    }
  });
}


const appRoutes: Routes = [
  { path: '', redirectTo: 'alloextracts', pathMatch: 'full' },
  {
    path: 'alloextracts',
    component: AlloExtractsComponent,
    canActivate: [MsalGuard]
  },
  { path: 'unauthorized', component: UnauthorizedComponent },
  { path: '**', redirectTo: 'alloextracts' }
];


export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();
  protectedResourceMap.set(
    environment.REDIRECT_URL,
    environment.SCOPES
  );

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: [...environment.SCOPES],
    },
    loginFailedRoute: '/unauthorized',
  };
}

export function initializeMsal(instance: IPublicClientApplication): () => Promise<void> {
  return () => instance.initialize();
}

export const FGA_API_ENDPOINT = new InjectionToken<string>('FGA_API_ENDPOINT');

@NgModule({
  declarations: [
    AppComponent,
    MenuComponent,
    AlloExtractsComponent,
    AlloPropertiesComponent,
    AlloReportsComponent,
    AlloRateMasterComponent,
    AlloHistoryComponent,
    UnauthorizedComponent,
    ToolbarComponent
  ],
  schemas: [
    CUSTOM_ELEMENTS_SCHEMA
  ],
  bootstrap: [
    AppComponent
  ], imports: [BrowserModule,
    AccordionModule,
    PanelModule,
    ButtonModule,
    RadioButtonModule,
    TableModule,
    MultiSelectModule,
    MenubarModule,
    InputTextModule,
    ToastModule,
    Select,
    MessageModule,
    ToggleButtonModule,
    DialogModule,
    DatePickerModule,
    CardModule,
    TooltipModule,
    CheckboxModule,
    ChartModule,
    ProgressBarModule,
    BrowserAnimationsModule,
    FormsModule,
    ReactiveFormsModule,
    MatListModule,
    MatMenuModule,
    MatIconModule,
    MatTabsModule,
    FgaModule,
    CommonModule,
    BreadcrumbComponent,
    NgxSpinnerModule.forRoot({ type: 'ball-clip-rotate' }),
    RouterModule.forRoot(appRoutes, { useHash: true }),
    ReactiveFormsModule.withConfig({ warnOnNgModelWithFormControl: 'never' })], providers: [
      AppGlobals,
      DelayedDispatchService,
      MessageService,
      ConstantsService,
      DatePipe,
      WebSocketService,
      // {
      //   provide: HTTP_INTERCEPTORS,
      //   useClass: AuthInterceptor,
      //   multi: true
      // },
      provideHttpClient(withInterceptorsFromDi()),
      {
        provide: HTTP_INTERCEPTORS,
        useClass: MsalInterceptor,
        multi: true
      },
      {
        provide: HTTP_INTERCEPTORS,
        useClass: CustomParameterInterceptor,
        multi: true
      },
      {
        provide: MSAL_INSTANCE,
        useFactory: MSALInstanceFactory
      },
      provideAppInitializer(() => {
        const msalInstance = inject(MSAL_INSTANCE);
        return msalInstance.initialize();
      }),
      {
        provide: MSAL_GUARD_CONFIG,
        useFactory: MSALGuardConfigFactory
      },
      {
        provide: MSAL_INTERCEPTOR_CONFIG,
        useFactory: MSALInterceptorConfigFactory
      },
      MsalService,
      MsalGuard,
      MsalBroadcastService,
      provideApollo(() => {
        return { cache: new InMemoryCache() };
      }),
      { provide: FGA_API_ENDPOINT, useValue: environment.APM_DS_URI },
      providePrimeNG({
        theme: {
          preset: Lara,
          options: {
            darkModeSelector: false 

          }
        }})
    ]
})
export class AppModule { }


